import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { clampFloatingRect, currentViewport, DOCK_LIMITS } from '../../context/WorkspaceLayoutContext';

/**
 * Pointer-driven move/resize for a fixed-position window kept inside the viewport.
 * Live movement writes left/top/width/height straight to the frame element (no React state per
 * move); the final rect is committed once on pointer-up. Dragging the title bar within
 * SNAP_DISTANCE of the left, right or bottom edge previews a dock zone, and releasing there
 * re-docks the window on that side.
 */

const SNAP_DISTANCE = 28;
const RESIZE_DIRECTIONS = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
/** Elements inside the title bar that must stay clickable instead of starting a drag. */
const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="button"], [role="tab"], [role="menuitem"], [contenteditable="true"]';

/**
 * Which dock side a pointer position is close enough to snap to.
 * @param {number} x - clientX.
 * @param {number} y - clientY.
 * @param {{width: number, height: number}} viewport - Viewport size.
 * @returns {'left'|'right'|'bottom'|null} Side.
 */
export function snapSideForPoint(x, y, viewport) {
  if (x <= SNAP_DISTANCE) return 'left';
  if (x >= viewport.width - SNAP_DISTANCE) return 'right';
  if (y >= viewport.height - SNAP_DISTANCE) return 'bottom';
  return null;
}

/**
 * Rect after resizing in a direction by (dx, dy), honouring min sizes.
 * @param {{x: number, y: number, width: number, height: number}} start - Rect at drag start.
 * @param {string} dir - One of n/s/e/w/ne/nw/se/sw.
 * @param {number} dx - Pointer delta x.
 * @param {number} dy - Pointer delta y.
 * @returns {{x: number, y: number, width: number, height: number}} Rect.
 */
export function resizeRect(start, dir, dx, dy) {
  const { minWidth, minHeight } = DOCK_LIMITS.floating;
  let { x, y, width, height } = start;
  if (dir.includes('e')) width = Math.max(minWidth, start.width + dx);
  if (dir.includes('s')) height = Math.max(minHeight, start.height + dy);
  if (dir.includes('w')) {
    width = Math.max(minWidth, start.width - dx);
    x = start.x + (start.width - width);
  }
  if (dir.includes('n')) {
    height = Math.max(minHeight, start.height - dy);
    y = start.y + (start.height - height);
  }
  return { x, y, width, height };
}

/**
 * Hook for a floating window.
 * @param {object} options - Options.
 * @param {{x: number, y: number, width: number, height: number}} options.rect - Committed rect.
 * @param {boolean} options.enabled - Whether the frame is currently floating.
 * @param {(rect: object) => void} options.onCommit - Persist a new rect.
 * @param {(side: 'left'|'right'|'bottom') => void} options.onSnap - Re-dock on a side.
 * @returns {{frameRef: React.RefObject<HTMLElement>, dragHandleProps: object, resizeHandles: JSX.Element|null, snapPreview: JSX.Element|null, interacting: boolean}} API.
 */
export function useFloatingWindow({ rect, enabled, onCommit, onSnap }) {
  const frameRef = useRef(null);
  const gestureRef = useRef(null);
  const frameReq = useRef(0);
  const [snapSide, setSnapSide] = useState(null);
  const [interacting, setInteracting] = useState(false);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const snapRef = useRef(onSnap);
  snapRef.current = onSnap;

  const applyRect = useCallback((next) => {
    const el = frameRef.current;
    if (!el) return;
    el.style.left = `${next.x}px`;
    el.style.top = `${next.y}px`;
    el.style.width = `${next.width}px`;
    el.style.height = `${next.height}px`;
  }, []);

  // Committed rect -> element before paint (also undoes a cancelled gesture). When the frame
  // stops floating, drop the inline geometry so the docked layout styles apply again.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    if (enabled && rect) {
      if (!gestureRef.current) applyRect(rect);
      return;
    }
    ['left', 'top', 'width', 'height'].forEach((prop) => el.style.removeProperty(prop));
  }, [enabled, rect, applyRect]);

  // Snap back inside the viewport when the window shrinks.
  useEffect(() => {
    if (!enabled) return undefined;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!rect || gestureRef.current) return;
        const clamped = clampFloatingRect(rect, currentViewport());
        if (clamped.x !== rect.x || clamped.y !== rect.y || clamped.width !== rect.width || clamped.height !== rect.height) {
          commitRef.current(clamped);
        }
      });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [enabled, rect]);

  const begin = useCallback((event, kind, dir) => {
    if (!enabled || event.button !== 0 || !rect) return;
    if (kind === 'move' && event.target.closest?.(INTERACTIVE_SELECTOR)) return;
    event.preventDefault();
    gestureRef.current = { kind, dir, pointerId: event.pointerId, sx: event.clientX, sy: event.clientY, start: { ...rect }, current: { ...rect }, snap: null };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* jsdom */
    }
    document.body.style.userSelect = 'none';
    setInteracting(true);
  }, [enabled, rect]);

  const move = useCallback((event) => {
    const g = gestureRef.current;
    if (!g || g.pointerId !== event.pointerId) return;
    const dx = event.clientX - g.sx;
    const dy = event.clientY - g.sy;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return;
    const viewport = currentViewport();
    if (g.kind === 'move') {
      g.current = clampFloatingRect({ ...g.start, x: g.start.x + dx, y: g.start.y + dy }, viewport);
      const side = snapSideForPoint(event.clientX, event.clientY, viewport);
      if (side !== g.snap) {
        g.snap = side;
        setSnapSide(side);
      }
    } else {
      g.current = clampFloatingRect(resizeRect(g.start, g.dir, dx, dy), viewport);
    }
    cancelAnimationFrame(frameReq.current);
    frameReq.current = requestAnimationFrame(() => applyRect(g.current));
  }, [applyRect]);

  const end = useCallback((event, cancelled = false) => {
    const g = gestureRef.current;
    if (!g || (event && g.pointerId !== event.pointerId)) return;
    gestureRef.current = null;
    cancelAnimationFrame(frameReq.current);
    document.body.style.removeProperty('user-select');
    setInteracting(false);
    setSnapSide(null);
    if (cancelled) {
      applyRect(g.start);
      return;
    }
    if (g.kind === 'move' && g.snap) {
      applyRect(g.start);
      snapRef.current(g.snap);
      return;
    }
    commitRef.current(g.current);
  }, [applyRect]);

  useEffect(() => () => {
    cancelAnimationFrame(frameReq.current);
    if (gestureRef.current) document.body.style.removeProperty('user-select');
  }, []);

  const gestureHandlers = useMemo(() => ({
    onPointerMove: move,
    onPointerUp: (event) => end(event),
    onPointerCancel: (event) => end(event, true),
  }), [move, end]);

  const dragHandleProps = useMemo(() => (enabled
    ? {
        onPointerDown: (event) => begin(event, 'move'),
        ...gestureHandlers,
        style: { cursor: interacting ? 'grabbing' : 'grab', touchAction: 'none' },
        'data-floating-drag-handle': 'true',
      }
    : {}), [enabled, begin, gestureHandlers, interacting]);

  const resizeHandles = enabled ? (
    <>
      {RESIZE_DIRECTIONS.map((dir) => (
        <Box
          key={dir}
          aria-hidden="true"
          data-resize-handle={dir}
          onPointerDown={(event) => begin(event, 'resize', dir)}
          {...gestureHandlers}
          sx={{
            position: 'absolute',
            zIndex: 3,
            touchAction: 'none',
            ...(dir.length === 2 ? { width: 14, height: 14 } : {}),
            ...(dir === 'n' ? { top: -4, left: 10, right: 10, height: 8, cursor: 'ns-resize' } : {}),
            ...(dir === 's' ? { bottom: -4, left: 10, right: 10, height: 8, cursor: 'ns-resize' } : {}),
            ...(dir === 'e' ? { right: -4, top: 10, bottom: 10, width: 8, cursor: 'ew-resize' } : {}),
            ...(dir === 'w' ? { left: -4, top: 10, bottom: 10, width: 8, cursor: 'ew-resize' } : {}),
            ...(dir === 'ne' ? { top: -4, right: -4, cursor: 'nesw-resize' } : {}),
            ...(dir === 'sw' ? { bottom: -4, left: -4, cursor: 'nesw-resize' } : {}),
            ...(dir === 'nw' ? { top: -4, left: -4, cursor: 'nwse-resize' } : {}),
            ...(dir === 'se' ? { bottom: -4, right: -4, cursor: 'nwse-resize' } : {}),
          }}
        />
      ))}
    </>
  ) : null;

  const snapPreview = snapSide ? (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        zIndex: 1240,
        pointerEvents: 'none',
        borderRadius: 'var(--q-radius-xl, 12px)',
        border: '2px dashed var(--q-accent, #4F3BD6)',
        bgcolor: 'var(--q-accent-tint, rgba(79,59,214,0.08))',
        opacity: 0.9,
        ...(snapSide === 'left' ? { top: 8, bottom: 8, left: 8, width: 'min(380px, 40vw)' } : {}),
        ...(snapSide === 'right' ? { top: 8, bottom: 8, right: 8, width: 'min(380px, 40vw)' } : {}),
        ...(snapSide === 'bottom' ? { left: 8, right: 8, bottom: 8, height: 'min(300px, 40vh)' } : {}),
      }}
    />
  ) : null;

  return { frameRef, dragHandleProps, resizeHandles, snapPreview, interacting };
}

export default useFloatingWindow;
